import { useEffect, useRef } from 'react';

/**
 * useAutoUpdate
 *
 * Automatically keeps the single-page application synchronized with production releases
 * without requiring manual user refreshes, cache clearing, or Ctrl+F5 on mobile devices.
 *
 * Checks for updates:
 * 1. Immediately on initial app load
 * 2. When the user returns to the tab or unlocks mobile phone (`visibilitychange`)
 * 3. When restored from mobile browser back-forward cache (`pageshow`)
 * 4. On window focus
 * 5. Periodic polling (every 30 seconds)
 * 6. Catches dynamic Vite chunk load errors and reloads once
 */
export function useAutoUpdate() {
  const isReloadingRef = useRef<boolean>(false);

  useEffect(() => {
    const currentVersion = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : '';
    if (!currentVersion) return;

    const checkVersion = async () => {
      if (isReloadingRef.current) return;

      try {
        const res = await fetch(`/version.json?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
          },
        });

        if (!res.ok) return;
        const data = await res.json();

        if (data && data.version && data.version !== currentVersion) {
          const reloadSessionKey = `tai_auto_reload_${data.version}`;
          if (sessionStorage.getItem(reloadSessionKey)) {
            // Avoid repeating reloads for the exact same version within the session
            return;
          }

          console.info(
            `[AutoUpdate] New version detected (local: ${currentVersion} -> server: ${data.version}). Refreshing...`
          );
          sessionStorage.setItem(reloadSessionKey, 'true');
          isReloadingRef.current = true;

          // Perform clean reload to pull latest hashed bundles
          window.location.reload();
        }
      } catch {
        // Silently skip if network is briefly offline
      }
    };

    // 1. Initial check shortly after mount
    const initialTimer = window.setTimeout(checkVersion, 2000);

    // 2. Mobile tab wakeup / visibility switch
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // 3. Mobile bfcache restore (when user resumes Safari / Chrome background tab)
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        checkVersion();
      }
    };
    window.addEventListener('pageshow', onPageShow);

    // 4. Window focus
    window.addEventListener('focus', checkVersion);

    // 5. Periodic background check (every 30s)
    const interval = window.setInterval(checkVersion, 30000);

    // 6. Handle chunk loading failures (e.g. when an old chunk was replaced on the server)
    const onGlobalError = (event: ErrorEvent) => {
      const msg = event.message || '';
      if (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Loading chunk') ||
        msg.includes('error loading dynamically imported module')
      ) {
        if (!isReloadingRef.current) {
          isReloadingRef.current = true;
          window.location.reload();
        }
      }
    };
    window.addEventListener('error', onGlobalError);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', checkVersion);
      window.removeEventListener('error', onGlobalError);
    };
  }, []);
}
